/**
 * ساختار مجوزهای دسترسی سیستم
 * هر category می‌تواند دارای subcategory باشد
 * و هر subcategory می‌تواند دارای permissions باشد
 */

export interface Permission {
  id: string;
  label: string;
  description?: string;
}

export interface SubCategory {
  id: string;
  label: string;
  description?: string;
  permissions: Permission[];
}

export interface PermissionCategory {
  id: string;
  label: string;
  description?: string;
  subCategories?: SubCategory[];
  permissions?: Permission[];
}

export const DATA_PERMISSIONS = {
  manage: 'data.manage',
  goldFunds: 'data.gold_funds.manage',
  goldHub: 'data.gold_hub.manage',
  money: 'data.money.manage',
  cpi: 'data.cpi.manage',
  selectedPortfolio: 'data.selected_portfolio.manage',
  industryAnalysis: 'data.industry_analysis.manage',
} as const;

export const PUBLIC_SUPPORT_PERMISSIONS = {
  viewAssigned: 'public_support.view_assigned',
  viewAll: 'public_support.view_all',
  assign: 'public_support.assign',
  manage: 'public_support.manage',
  reply: 'public_support.reply',
  contactsView: 'public_support.contacts.view',
  historyView: 'public_support.history.view',
  knowledgeView: 'public_support.knowledge.view',
  knowledgeManage: 'public_support.knowledge.manage',
  knowledgeIngest: 'public_support.knowledge.ingest',
  diagnosticsView: 'public_support.diagnostics.view',
  operationsView: 'public_support.operations.view',
  operationsManage: 'public_support.operations.manage',
} as const;

/**
 * ساختار کامل مجوزهای سیستم
 */
export const PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    id: 'users',
    label: 'کاربران',
    description: 'مدیریت کاربران سیستم',
    permissions: [
      {
        id: 'users.download_excel',
        label: 'دانلود اکسل',
        description: 'امکان دانلود لیست کاربران به صورت اکسل'
      },
      {
        id: 'users.details',
        label: 'جزئیات کاربران',
        description: 'مشاهده جزئیات کامل کاربران'
      },
      {
        id: 'users.toggle_active',
        label: 'فعال و غیرفعال کاربر',
        description: 'فعال یا غیرفعال کردن حساب کاربری'
      }
    ]
  },
  {
    id: 'financial',
    label: 'مدیریت امور مالی',
    description: 'مدیریت تراکنش‌ها و امور مالی',
    subCategories: [
      {
        id: 'financial.transactions',
        label: 'مدیریت تراکنش‌ها',
        description: 'مدیریت تراکنش‌های مالی',
        permissions: [
          {
            id: 'financial.transactions.create',
            label: 'ثبت تراکنش جدید',
            description: 'امکان ثبت تراکنش جدید'
          },
          {
            id: 'financial.transactions.download_excel',
            label: 'دریافت اکسل',
            description: 'دانلود لیست تراکنش‌ها به صورت اکسل'
          },
          {
            id: 'financial.transactions.details',
            label: 'جزئیات پرداخت',
            description: 'مشاهده جزئیات پرداخت‌ها'
          }
        ]
      },
      {
        id: 'financial.discount_codes',
        label: 'کدهای تخفیف',
        description: 'مدیریت کدهای تخفیف',
        permissions: [
          {
            id: 'financial.discount_codes.create',
            label: 'ایجاد کد تخفیف',
            description: 'امکان ایجاد کد تخفیف جدید'
          }
        ]
      }
    ]
  },
  {
    id: 'subscriptions',
    label: 'مدیریت اشتراک‌ها',
    description: 'مدیریت طرح‌های اشتراک',
    permissions: [
      {
        id: 'subscriptions.create_plan',
        label: 'ایجاد طرح',
        description: 'ایجاد طرح اشتراک جدید'
      },
      {
        id: 'subscriptions.edit_plan',
        label: 'ویرایش طرح',
        description: 'ویرایش طرح‌های موجود'
      },
      {
        id: 'subscriptions.delete_plan',
        label: 'حذف طرح',
        description: 'حذف طرح‌های اشتراک'
      },
      {
        id: 'subscriptions.download_excel',
        label: 'خروجی اکسل',
        description: 'دانلود لیست اشتراک‌ها به صورت اکسل'
      },
      {
        id: 'subscriptions.transaction_details',
        label: 'جزئیات تراکنش',
        description: 'مشاهده جزئیات تراکنش‌های اشتراک'
      }
    ]
  },
  {
    id: 'courses',
    label: 'مدیریت دوره‌ها',
    description: 'مدیریت دوره‌های آموزشی',
    permissions: [
      {
        id: 'courses.categories',
        label: 'دسته‌بندی‌ها',
        description: 'مدیریت دسته‌بندی‌های دوره‌ها'
      },
      {
        id: 'courses.instructors',
        label: 'اساتید',
        description: 'مدیریت اساتید دوره‌ها'
      },
      {
        id: 'courses.orders',
        label: 'سفارشات',
        description: 'مدیریت سفارشات دوره‌ها'
      },
      {
        id: 'courses.assign_course',
        label: 'تخصیص دوره',
        description: 'تخصیص دوره به کاربر'
      },
      {
        id: 'courses.create',
        label: 'ایجاد دوره',
        description: 'ایجاد دوره جدید'
      },
      {
        id: 'courses.edit_delete',
        label: 'ویرایش و حذف دوره',
        description: 'ویرایش یا حذف دوره‌های موجود'
      }
    ]
  },
  {
    id: 'analyses',
    label: 'مدیریت تحلیل‌ها',
    description: 'مدیریت تحلیل‌ها و گزارش‌ها',
    permissions: [
      {
        id: 'analyses.create',
        label: 'ایجاد تحلیل',
        description: 'ایجاد تحلیل جدید'
      },
      {
        id: 'analyses.edit',
        label: 'ویرایش تحلیل',
        description: 'ویرایش تحلیل‌های موجود'
      },
      {
        id: 'analyses.delete',
        label: 'حذف تحلیل',
        description: 'حذف تحلیل‌ها'
      }
    ]
  },
  {
    id: 'data',
    label: 'مدیریت داده‌ها',
    description: 'تعیین دسترسی به صفحه مدیریت داده یا هر جدول به‌صورت مستقل',
    permissions: [
      {
        id: DATA_PERMISSIONS.manage,
        label: 'دسترسی کامل مدیریت داده',
        description: 'دسترسی به صفحه مدیریت داده و همه جدول‌های آن'
      },
      {
        id: DATA_PERMISSIONS.goldFunds,
        label: 'صندوق‌های طلا',
        description: 'مشاهده و ویرایش جدول داده‌های صندوق‌های طلا'
      },
      {
        id: DATA_PERMISSIONS.goldHub,
        label: 'طلای جهانی',
        description: 'آپلود و به‌روزرسانی داده‌های ذخایر، عرضه و تقاضا و ETF طلا'
      },
      {
        id: DATA_PERMISSIONS.money,
        label: 'نقدینگی و پول',
        description: 'مشاهده، ورود، ویرایش، حذف و آپلود داده‌های پولی'
      },
      {
        id: DATA_PERMISSIONS.cpi,
        label: 'CPI ایران و آمریکا',
        description: 'مشاهده، ورود، ویرایش، حذف و آپلود داده‌های CPI'
      },
      {
        id: DATA_PERMISSIONS.selectedPortfolio,
        label: 'سبدهای منتخب',
        description: 'مدیریت کامل محتوای صفحه selected-portfolio و همه داده‌های وابسته'
      },
      {
        id: DATA_PERMISSIONS.industryAnalysis,
        label: 'گزارش تحلیلی صنایع',
        description: 'دانلود نمونه، جایگزینی اکسل و حذف داده‌های گزارش تحلیلی صنایع'
      }
    ]
  },
  {
    id: 'media',
    label: 'مرکز رسانه',
    description: 'مدیریت فایل‌های رسانه‌ای',
    permissions: [
      {
        id: 'media.manage',
        label: 'مدیریت مرکز رسانه',
        description: 'دسترسی کامل به مرکز رسانه'
      }
    ]
  },
  {
    id: 'tickets',
    label: 'مدیریت تیکت‌ها',
    description: 'مدیریت تیکت‌های پشتیبانی',
    permissions: [
      {
        id: 'tickets.manage',
        label: 'مدیریت تیکت‌ها',
        description: 'دسترسی کامل به مدیریت تیکت‌ها'
      }
    ]
  },
  {
    id: 'qa',
    label: 'مدیریت پرسش و پاسخ',
    description: 'مدیریت سوالات و پاسخ‌ها',
    permissions: [
      {
        id: 'qa.create_category',
        label: 'ایجاد دسته‌بندی',
        description: 'ایجاد دسته‌بندی جدید برای سوالات'
      },
      {
        id: 'qa.create_question',
        label: 'ایجاد سوال جدید',
        description: 'ایجاد سوال جدید'
      },
      {
        id: 'qa.edit_question',
        label: 'ویرایش سوال',
        description: 'ویرایش سوالات موجود'
      },
      {
        id: 'qa.delete_question',
        label: 'حذف سوال',
        description: 'حذف سوالات'
      }
    ]
  },
  {
    id: 'consulting',
    label: 'درخواست‌های مشاوره',
    description: 'مدیریت درخواست‌های مشاوره',
    permissions: [
      {
        id: 'consulting.manage',
        label: 'دسترسی به درخواست‌های مشاوره',
        description: 'مشاهده و پیگیری درخواست‌های مشاوره تخصیص داده‌شده'
      }
    ]
  },
  {
    id: 'notifications',
    label: 'مدیریت اعلان‌ها',
    description: 'مدیریت اعلان‌های سیستم',
    permissions: [
      {
        id: 'notifications.manage',
        label: 'مدیریت اعلان‌ها',
        description: 'دسترسی کامل به مدیریت اعلان‌ها'
      }
    ]
  },
  {
    id: 'public_support',
    label: 'پشتیبانی عمومی',
    description: 'مشاهده و مدیریت گفت‌وگوهای پشتیبانی عمومی سایت',
    permissions: [
      {
        id: PUBLIC_SUPPORT_PERMISSIONS.viewAssigned,
        label: 'مشاهده گفت‌وگوهای واگذارشده',
        description: 'مشاهده گفت‌وگوهایی که به کارشناس واگذار شده‌اند'
      },
      {
        id: PUBLIC_SUPPORT_PERMISSIONS.viewAll,
        label: 'مشاهده همه گفت‌وگوها',
        description: 'مشاهده همه گفت‌وگوهای پشتیبانی عمومی'
      },
      {
        id: PUBLIC_SUPPORT_PERMISSIONS.assign,
        label: 'واگذاری گفت‌وگو',
        description: 'واگذاری، واگذاری مجدد و حذف واگذاری'
      },
      {
        id: PUBLIC_SUPPORT_PERMISSIONS.manage,
        label: 'مدیریت گردش کار',
        description: 'تغییر واحد، اولویت و وضعیت گفت‌وگو'
      },
      {
        id: PUBLIC_SUPPORT_PERMISSIONS.reply,
        label: 'پاسخ به بازدیدکننده',
        description: 'ارسال پاسخ عمومی با عنوان کارشناس'
      },
      {
        id: PUBLIC_SUPPORT_PERMISSIONS.contactsView,
        label: 'مشاهده راه‌های ارتباطی',
        description: 'نمایش اطلاعات کامل تماس پس از اقدام مستقیم'
      },
      {
        id: PUBLIC_SUPPORT_PERMISSIONS.historyView,
        label: 'مشاهده سابقه تغییرات',
        description: 'مشاهده سابقه واگذاری و تغییرات گردش کار'
      },
      {
        id: PUBLIC_SUPPORT_PERMISSIONS.knowledgeView,
        label: 'مشاهده دانش عمومی',
        description: 'مشاهده منابع و وضعیت نمایه‌سازی دانش عمومی'
      },
      {
        id: PUBLIC_SUPPORT_PERMISSIONS.knowledgeManage,
        label: 'مدیریت دانش عمومی',
        description: 'مدیریت منابع و مقاله‌های تأییدشده پشتیبانی'
      },
      {
        id: PUBLIC_SUPPORT_PERMISSIONS.knowledgeIngest,
        label: 'به‌روزرسانی نمایه دانش',
        description: 'ثبت درخواست نمایه‌سازی منابع عمومی'
      },
      {
        id: PUBLIC_SUPPORT_PERMISSIONS.diagnosticsView,
        label: 'مشاهده تشخیص دانش',
        description: 'مشاهده کفایت، تازگی و منابع پاسخ‌ها'
      },
      {
        id: PUBLIC_SUPPORT_PERMISSIONS.operationsView,
        label: 'مشاهده عملیات پشتیبانی',
        description: 'مشاهده وضعیت و سیگنال‌های عملیاتی پشتیبانی عمومی'
      },
      {
        id: PUBLIC_SUPPORT_PERMISSIONS.operationsManage,
        label: 'مدیریت حالت پشتیبانی',
        description: 'مدیریت حالت خودکار و تغییر دستی دسترس‌پذیری'
      }
    ]
  },
  {
    id: 'ai_agent',
    label: 'مدیریت هوش مصنوعی و Agent',
    description: 'دسترسی‌های تفکیک‌شدهٔ عملیات و حسابداری Agent',
    permissions: [
      {
        id: 'ai_agent.view',
        label: 'مشاهده نمای کلی',
        description: 'مشاهده KPIها، مصرف و سلامت خلاصه Agent'
      },
      {
        id: 'ai_agent.users.view',
        label: 'مشاهده کاربران Agent',
        description: 'مشاهده کاربران، کیف‌پول و جزئیات مصرف'
      },
      {
        id: 'ai_agent.wallet.adjust',
        label: 'تغییر اعتبار Wallet',
        description: 'افزایش یا کاهش اتمیک اعتبار AI با ثبت Audit'
      },
      {
        id: 'ai_agent.usage.view',
        label: 'مشاهده مصرف و هزینه',
        description: 'مشاهده درخواست‌ها، tokenها و هزینه ثبت‌شده'
      },
      {
        id: 'ai_agent.conversations.view',
        label: 'مشاهده مکالمه‌ها',
        description: 'مشاهده sessionها و پیام‌های پالایش‌شده'
      },
      {
        id: 'ai_agent.settings.view',
        label: 'مشاهده تنظیمات',
        description: 'مشاهده کاتالوگ امن مدل‌ها'
      },
      {
        id: 'ai_agent.settings.manage',
        label: 'مدیریت تنظیمات',
        description: 'مجوز تغییر تنظیمات در صورت وجود منبع قابل‌ویرایش معتبر'
      },
      {
        id: 'ai_agent.health.view',
        label: 'مشاهده سلامت',
        description: 'مشاهده سلامت سرویس‌ها، وابستگی‌ها و صف‌ها'
      },
      {
        id: 'ai_agent.audit.view',
        label: 'مشاهده Audit',
        description: 'مشاهده رویدادهای حساس مدیریتی Agent'
      }
    ]
  },
  {
    id: 'rag',
    label: 'مدیریت RAG',
    description: 'مدیریت سیستم RAG',
    permissions: [
      {
        id: 'rag.manage',
        label: 'مدیریت RAG',
        description: 'دسترسی کامل به مدیریت RAG'
      }
    ]
  },
  {
    id: 'portfolio',
    label: 'سبدگردانی',
    description: 'مدیریت سبدگردانی',
    permissions: [
      {
        id: 'portfolio.manage',
        label: 'سبدگردانی',
        description: 'دسترسی کامل به سبدگردانی'
      }
    ]
  },
  {
    id: 'credit',
    label: 'اعتبار هوش مصنوعی',
    description: 'مدیریت اعتبار AI و خریدها',
    permissions: [
      { id: 'credit.view', label: 'مشاهده کیف‌پول', description: 'مشاهده موجودی و تاریخچه اعتبار کاربران' },
      { id: 'credit.grant', label: 'اعطای اعتبار', description: 'اعطای اعتبار تکی به کاربر' },
      { id: 'credit.debit', label: 'برداشت اعتبار', description: 'کسر اعتبار از کیف‌پول کاربر' },
      { id: 'credit.refund', label: 'بازگشت خرید', description: 'برگشت اعتبار خریداری‌شده' },
      { id: 'credit.bulk_grant', label: 'اعطای گروهی', description: 'اعطای اعتبار به چند کاربر' },
      { id: 'credit.operations', label: 'عملیات حسابداری', description: 'دسترسی به لاگ عملیات حساس' },
      { id: 'credit.pricing.manage', label: 'مدیریت قیمت‌گذاری', description: 'تنظیم سیاست قیمت و سهمیه اشتراک' },
      { id: 'credit.packages.view', label: 'مشاهده بسته‌ها', description: 'مشاهده فهرست بسته‌های اعتبار' },
      { id: 'credit.packages.create', label: 'ایجاد بسته', description: 'تعریف بسته اعتبار جدید' },
      { id: 'credit.packages.update', label: 'ویرایش بسته', description: 'ویرایش مشخصات و قیمت بسته اعتبار' },
      { id: 'credit.packages.toggle', label: 'تغییر وضعیت بسته', description: 'فعال، غیرفعال یا بایگانی‌کردن بسته' },
      { id: 'credit.purchases.view', label: 'مشاهده خریدها', description: 'مشاهده سفارش‌های خرید اعتبار' },
      { id: 'credit.packages.manage', label: 'مدیریت بسته‌ها (قدیمی)', description: 'مجوز سازگاری موقت برای مدیران فعلی' },
      { id: 'credit.analytics.view', label: 'آمار اعتبار', description: 'مشاهده آمار کلی اعتبار پلتفرم' },
    ]
  }
];

/**
 * تبدیل تمام permissions به یک آرایه مسطح برای استفاده در API
 */
export function getAllPermissions(): string[] {
  const permissions: string[] = [];
  
  PERMISSION_CATEGORIES.forEach(category => {
    // اگر category دارای permissions مستقیم باشد
    if (category.permissions) {
      category.permissions.forEach(perm => {
        permissions.push(perm.id);
      });
    }
    
    // اگر category دارای subCategories باشد
    if (category.subCategories) {
      category.subCategories.forEach(subCategory => {
        subCategory.permissions.forEach(perm => {
          permissions.push(perm.id);
        });
      });
    }
  });
  
  return permissions;
}

/**
 * بررسی اینکه آیا یک permission در لیست permissions انتخاب شده وجود دارد
 */
export function hasPermission(selectedPermissions: string[], permissionId: string): boolean {
  return selectedPermissions.includes(permissionId);
}

/**
 * بررسی اینکه آیا تمام permissions یک category انتخاب شده‌اند
 */
export function isCategoryFullySelected(
  category: PermissionCategory,
  selectedPermissions: string[]
): boolean {
  const categoryPermissions = getCategoryPermissions(category);
  return categoryPermissions.length > 0 && 
         categoryPermissions.every(perm => selectedPermissions.includes(perm));
}

/**
 * بررسی اینکه آیا حداقل یک permission از یک category انتخاب شده است
 */
export function isCategoryPartiallySelected(
  category: PermissionCategory,
  selectedPermissions: string[]
): boolean {
  const categoryPermissions = getCategoryPermissions(category);
  return categoryPermissions.some(perm => selectedPermissions.includes(perm));
}

/**
 * دریافت تمام permissions یک category (شامل subcategories)
 */
export function getCategoryPermissions(category: PermissionCategory): string[] {
  const permissions: string[] = [];
  
  if (category.permissions) {
    category.permissions.forEach(perm => {
      permissions.push(perm.id);
    });
  }
  
  if (category.subCategories) {
    category.subCategories.forEach(subCategory => {
      subCategory.permissions.forEach(perm => {
        permissions.push(perm.id);
      });
    });
  }
  
  return permissions;
}

/**
 * دریافت تمام permissions یک subcategory
 */
export function getSubCategoryPermissions(subCategory: SubCategory): string[] {
  return subCategory.permissions.map(perm => perm.id);
}
